import { useState, useEffect } from 'react'
import {
    Toolbar,
    ListItem,
    IconButton,
    Grid,
    Skeleton,
    Pagination,
    Switch
} from '@mui/material'
import {
    faInfoCircle,
    faPen,
    faTrash,
    faUser
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link, useNavigate } from 'react-router-dom'
import { sitesRoutes, usersRoutes } from '../../routes'
import { changeTypeUser } from '../../config'
import { UserListDataModal } from '../../modals'
import { LoadingPage } from '../../pages'
import { useSitesContext, useUsersContext } from '../../context'
import { useStatus } from '../../context/Status.context'
import { UserListDataDialog } from '../../dialogs'
import * as XLSX from 'xlsx'

export const normalizeRutDisplay = (rut) => {
    if (!rut) return ''
    const str = String(rut).trim()
    if (str.includes('.')) {
        return str
    }
    const clean = str.replace(/[^0-9kK-]/g, '')
    let body = ''
    let dv = ''
    if (clean.includes('-')) {
        const parts = clean.split('-')
        body = parts[0]
        dv = parts[1]
    } else if (clean.length >= 2) {
        body = clean.slice(0, -1)
        dv = clean.slice(-1)
    } else {
        return str
    }

    const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    return dv ? `${formattedBody}-${dv}` : formattedBody
}

const UsersList = ({
    height,
    hableButton,
    email,
    nombre,
    obra,
    run,
    firma,
    usersToDisplay,
    exportUsuarios
}) => {
    const { loadingUsers, getUsers } = useUsersContext()
    const { setstatus } = useStatus()
    const [usuarios, setUsuarios] = useState([])
    const [usuariosCache, setUsuariosCache] = useState([])
    const [open, setOpen] = useState(false)
    const [userModal, setUserModal] = useState({})
    const [indice, setIndice] = useState(0)
    const [checked, setCheck] = useState(false)
    const [permissionsReports, setPermissionsReports] = useState([])
    const [permissionsUsers, setPermissionsUsers] = useState([])
    const [skeletons, setSkeletons] = useState([])
    const [group, setGroup] = useState()
    const [pages, setPages] = useState(0)
    const [page, setPage] = useState(1)
    const [estaFiltrando, setEstaFiltrando] = useState(false)

    useEffect(() => {
        const skeletonsCache = []
        for (let i = 0; i < 14; i++) {
            skeletonsCache.push(i)
        }
        setSkeletons(skeletonsCache)
    }, [])

    useEffect(() => {
        if (exportUsuarios) {
            const rows = usuariosCache.map((usuario) => {
                const newRow = {
                    Nombre: usuario.name,
                    Apellido: usuario.lastName,
                    'R.U.T': normalizeRutDisplay(usuario.rut),
                    Firma: usuario.sign ? 'SI' : 'NO'
                }
                return newRow
            })
            const workbook = XLSX.utils.book_new()
            const worksheet = XLSX.utils.json_to_sheet(rows)
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios')
            XLSX.writeFile(workbook, 'usuarios.xlsx')
        }
    }, [exportUsuarios])

    useEffect(() => {
        if (group) {
            setUsuarios(group[page])
        }
    }, [page, group])

    const handleChange = async (event) => {
        const newCheckedState = event.target.checked
        const originalCheckedState = checked
        setCheck(newCheckedState) // Optimistically update UI

        try {
            await usersRoutes.activateUser(userModal.email)
            // On success, update the master list
            getUsers()
        } catch (error) {
            console.error('Error toggling user activation:', error)
            alert('Error al cambiar el estado del usuario.')
            setCheck(originalCheckedState) // Revert on error
        }
    }

    const handleToggleMailNotification = async (user) => {
        const newValue = !user.mailNotification
        try {
            await usersRoutes.editUser({ mailNotification: newValue }, user._id)
            getUsers()
        } catch (error) {
            console.error('Error toggling mail notification:', error)
            alert('Error al actualizar la notificación por correo.')
        }
    }

    const openModal = (user, i) => {
        console.log('User object in openModal:', user)
        setUserModal(user)
        setCheck(user.enabled)
        // Correctly set permissions from the user's role object
        if (user.role && user.role.permissions) {
            const reportPerms = user.role.permissions.filter(
                (p) => p && p.key && p.key.includes('REPORT')
            )
            const userPerms = user.role.permissions.filter(
                (p) => p && p.key && p.key.includes('USER')
            )
            setPermissionsReports(reportPerms)
            setPermissionsUsers(userPerms)
        } else {
            setPermissionsReports([])
            setPermissionsUsers([])
        }
        setIndice(i)
        setOpen(true)
    }
    const closeModal = () => {
        setOpen(false)
    }

    useEffect(() => {
        let usuariosTem = usersToDisplay || []

        if (nombre) {
            usuariosTem = buscarUsuario(nombre, usuariosTem)
        }
        if (email) {
            usuariosTem = buscarPorCorreo(email, usuariosTem)
        }
        if (run) {
            usuariosTem = buscarPorRut(run, usuariosTem)
        }
        if (firma) {
            usuariosTem = buscarFirma(usuariosTem, firma)
        }
        if (obra) {
            usuariosTem = usuariosTem.filter((usuario) => {
                if (usuario.obras && Array.isArray(usuario.obras)) {
                    return usuario.obras.some((o) => {
                        if (typeof o === 'object' && o !== null) {
                            return String(o._id || o.id) === String(obra)
                        }
                        return String(o) === String(obra)
                    })
                }
                return false
            })
        }

        const isCurrentlyFiltered = nombre || email || run || firma !== 'Todas' || obra
        setEstaFiltrando(isCurrentlyFiltered)

        const sortedUsers = [...usuariosTem].sort((a, b) => {
            const nameA = `${a.name} ${a.lastName}`.toUpperCase()
            const nameB = `${b.name} ${b.lastName}`.toUpperCase()
            if (nameA < nameB) return -1
            if (nameA > nameB) return 1
            return 0
        })

        setUsuariosCache(sortedUsers)
    }, [email, nombre, obra, run, firma, usersToDisplay])

    useEffect(() => {
        if (usuariosCache) {
            const usersGroup = {}
            let initIndex = 0
            let numberUsersByGroup = 14
            const pagesCache = Math.floor(usuariosCache.length / 14) + 1
            setPages(pagesCache)
            for (let i = 1; i < pagesCache + 1; i++) {
                if (!usersGroup[i]) {
                    usersGroup[i] = usuariosCache.slice(initIndex, numberUsersByGroup)
                    initIndex = initIndex + 14
                    numberUsersByGroup = numberUsersByGroup + 14
                }
            }
            setGroup(usersGroup)
        }
    }, [usuariosCache])

    const changePage = (e, value) => {
        setPage(Number(value))
    }

    const deleteUser = (userId, userName, userLastName, userRole) => {
        if (userRole && userRole.dbName === 'admin') {
            alert('No es posible eliminar un Administrador.')
        } else {
            if (
                confirm(
                    `Removerá al usuario ${userName} ${userLastName}. Confirme la acción.`
                )
            ) {
                usersRoutes.removeUser(userId).then(async (data) => {
                    if (data) {
                        getUsers() // Refresh list from context
                    }
                })
            }
        }
    }

    const well = {
        /* height: 70, */
        borderRadius: 10,
        boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.08)',
        fontSize: 14
    }

    const buscarUsuario = (value = '', usuariosTem) => {
        const searchTerm = value.toUpperCase() // Eliminamos el trim() agresivo para permitir buscar "Carlos "
        if (searchTerm.length > 0) {
            return usuariosTem.filter((usuario) => {
                const name = (usuario.name || '').toUpperCase()
                const lastName = (usuario.lastName || '').toUpperCase()
                const fullName = `${name} ${lastName}`.trim()
                return fullName.includes(searchTerm.trim())
            })
        }
        return usuariosTem
    }

    const buscarPorCorreo = (value = new String(), usuariosTem) => {
        console.log('Correo: ', value)
        if (value.length > 3) {
            const usuarios = usuariosTem.filter((usuario) => {
                const email = usuario.email
                if (email) {
                    if (email.toUpperCase().match(value.toUpperCase())) {
                        return usuario
                    } else {
                        return null
                    }
                } else {
                    return null
                }
            })
            return usuarios
        } else {
            const usuarios = usuariosTem
            return usuarios
        }
    }

    const buscarPorRut = (value = new String(), usuariosTem) => {
        console.log('Rut: ', value)
        const normalizedValue = value.replace(/\./g, '')
        if (normalizedValue.length > 3) {
            const usuarios = usuariosTem.filter((usuario) => {
                if (usuario.rut) {
                    const normalizedRut = usuario.rut.replace(/\./g, '')
                    if (normalizedRut.match(normalizedValue)) {
                        return usuario
                    } else {
                        return null
                    }
                } else {
                    return null
                }
            })
            return usuarios
        } else {
            const usuarios = usuariosTem
            return usuarios
        }
    }

    const buscarFirma = (usuariosTem, firma) => {
        const usuarios = usuariosTem.filter((usuario) => {
            if (firma === 'Sin Firma') {
                if (!usuario.sign) {
                    return usuario
                } else {
                    return null
                }
            } else if (firma === 'Con firma') {
                if (usuario.sign) {
                    return usuario
                } else {
                    return null
                }
            } else {
                return usuario
            }
        })
        return usuarios
    }

    const buscarPorRol = (value) => {
        console.log(value)
        if (value === 'TODOS LOS ROLES') {
            setUsuarios(usuariosCache)
        } else {
            const usuarios = usuariosCache.filter((usuario) => {
                if (usuario.roles && usuario.roles.includes(value)) {
                    return usuario
                } else {
                    return null
                }
            })
            setUsuarios(usuarios)
        }
    }

    const buscarPorObra = (value) => {
        console.log(value)
        if (value === 'TODAS LAS OBRAS') {
            setUsuarios(usuariosCache)
        } else {
            const usuarios = usuariosCache.filter((usuario) => {
                if (usuario.obras[0] && usuario.obras[0].descripcion) {
                    if (usuario.obras[0].descripcion.match(value)) {
                        return usuario
                    } else {
                        return null
                    }
                } else {
                    return null
                }
            })
            setUsuarios(usuarios)
        }
    }

    return (
        <div style={{ height: 'calc(100vh - 200px)', position: 'relative' }}>
            <UserHeader />
            {hableButton && (
                <div style={{ overflowY: 'auto', height: 'calc(100vh - 300px)' }}>
                    {loadingUsers &&
						skeletons.map((i) => {
						    return <LoadingSkeleton key={i} />
						})}
                    {usuarios &&
						usuarios.length > 0 &&
						!loadingUsers &&
						usuarios.map((e, n) => {
						    return (
						        <UserItem
						            key={n}
						            user={e}
						            index={n}
						            openModal={openModal}
						            deleteUser={deleteUser}
						            handleToggleMailNotification={handleToggleMailNotification}
						        />
						    )
						})}
                    {open && (
                        <UserListDataDialog
                            open={open}
                            userModal={userModal}
                            handleChange={handleChange}
                            checked={checked}
                            permissionsReports={permissionsReports}
                            permissionsUsers={permissionsUsers}
                            closeModal={closeModal}
                            setstatus={setstatus}
                        />
                    )}
                </div>
            )}
            {!hableButton && (
                <div
                    style={{
                        textAlign: 'center',
                        width: '100%',
                        height: '50%',
                        paddingTop: 50
                    }}
                >
                    <h1>Requiere conexión a internet para administración de usuarios</h1>
                </div>
            )}
            <Pagination
                style={{
                    position: 'absolute',
                    right: 20,
                    bottom: 20
                }}
                count={pages}
                page={page}
                onChange={changePage}
                color="primary"
            />
        </div>
    )
}

const UserHeader = () => {
    return (
        <Grid container style={{ fontSize: 10, borderBottom: '1px #ccc solid', height: 40, alignItems: 'center' }}>
            <Grid item xs={0.5} style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: 25 }}></div>
            </Grid>
            <Grid item xs={2}>
                <p style={{ paddingLeft: 10 }}><strong>Nombre de usuario</strong></p>
            </Grid>
            <Grid item xs={2.5}>
                <p style={{ paddingLeft: 10 }}><strong>Correo electrónico</strong></p>
            </Grid>
            <Grid item xs={1}>
                <p style={{ paddingLeft: 10 }}><strong>RUN</strong></p>
            </Grid>
            <Grid item xs={1}>
                <p style={{ paddingLeft: 10 }}><strong>Rol</strong></p>
            </Grid>
            <Grid item xs={1.5}>
                <p style={{ paddingLeft: 10 }}><strong>Faena / Obra</strong></p>
            </Grid>
            <Grid item xs={1} style={{ textAlign: 'center' }}>
                <p><strong>Estado</strong></p>
            </Grid>
            <Grid item xs={1} style={{ textAlign: 'center' }}>
                <p><strong>Notif.</strong></p>
            </Grid>
            <Grid item xs={1.5} style={{ textAlign: 'center' }}>
                <p><strong>Acciones</strong></p>
            </Grid>
        </Grid>
    )
}

const UserItem = ({
    user,
    index,
    openModal,
    deleteUser,
    handleToggleMailNotification
}) => {
    const [e, setUser] = useState()
    useEffect(() => {
        setUser(user)
    }, [user])

    const hasValidEmail =
		e?.email && e.email !== 'x@x.xx' && !e.email.includes('@placeholder.icv')

    return (
        <>
            {e && (
                <Grid
                    container
                    style={{
                        fontSize: 10,
                        borderBottom: '1px #ccc solid',
                        alignItems: 'center',
                        minHeight: '41px'
                    }}
                >
                    <Grid item xs={0.5} style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: 30, display: 'flex', justifyContent: 'center' }}>
                            <img
                                style={{
                                    height: 25,
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    width: 25
                                }}
                                src={
                                    !e.imageUrl ? '../assets/no-profile-image.webp' : e.imageUrl
                                }
                                alt=""
                            />
                        </div>
                    </Grid>
                    <Grid item xs={2} style={{ paddingLeft: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <p title={`${e.name || ''} ${e.lastName || ''}`}>{`${e.name || ''} ${e.lastName || ''}`}</p>
                    </Grid>
                    <Grid item xs={2.5} style={{ paddingLeft: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <p title={e.email}>{e.email === 'x@x.xx' ? '----' : e.email}</p>
                    </Grid>
                    <Grid item xs={1} style={{ paddingLeft: 10 }}>
                        <p>{normalizeRutDisplay(e.rut)}</p>
                    </Grid>
                    <Grid item xs={1} style={{ paddingLeft: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.role && e.role.name ? <p title={e.role.name}>{e.role.name}</p> : <p>N/A</p>}
                    </Grid>
                    <Grid item xs={1.5} style={{ paddingLeft: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.obras && e.obras.length > 0 ? (
                            e.obras.map((obra, i) => (
                                <p key={i} style={{ margin: 0 }} title={typeof obra === 'object' && obra.descripcion ? obra.descripcion : 'N/A'}>
                                    {typeof obra === 'object' && obra.descripcion
                                        ? obra.descripcion
                                        : 'N/A'}
                                </p>
                            ))
                        ) : (
                            <p></p>
                        )}
                    </Grid>
                    <Grid item xs={1} style={{ display: 'flex', justifyContent: 'center' }}>
                        {e.enabled && (
                            <p
                                style={{
                                    borderRadius: 5,
                                    width: '80%',
                                    textAlign: 'center',
                                    backgroundColor: '#C3EBD4',
                                    padding: '3px 5px'
                                }}
                            >
								ACTIVO
                            </p>
                        )}
                        {!e.enabled && (
                            <p
                                style={{
                                    borderRadius: 5,
                                    width: '80%',
                                    textAlign: 'center',
                                    backgroundColor: '#F9F9F9',
                                    padding: '3px 5px'
                                }}
                            >
								INACTIVO
                            </p>
                        )}
                    </Grid>
                    <Grid item xs={1} style={{ display: 'flex', justifyContent: 'center' }}>
                        {hasValidEmail ? (
                            <Switch
                                size="small"
                                checked={!!e.mailNotification}
                                onChange={() => handleToggleMailNotification(e)}
                                inputProps={{ 'aria-label': 'Notificación por correo' }}
                            />
                        ) : (
                            <p style={{ color: '#ccc' }}>N/A</p>
                        )}
                    </Grid>
                    <Grid item xs={1.5} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                            title="Información del usuario"
                            onClick={() => openModal(e, index)}
                            size="small"
                        >
                            <FontAwesomeIcon style={{ width: 14 }} icon={faInfoCircle} />
                        </IconButton>
                        <Link to={`/edit-user/${e._id}`}>
                            <IconButton title="Editar usuario" size="small">
                                <FontAwesomeIcon style={{ width: 14 }} icon={faPen} />
                            </IconButton>
                        </Link>
                        <IconButton
                            title="Borrar usuario"
                            onClick={() => {
                                deleteUser(e._id, e.name, e.lastName, e.role.name)
                            }}
                            size="small"
                        >
                            <FontAwesomeIcon style={{ width: 14 }} icon={faTrash} />
                        </IconButton>
                    </Grid>
                </Grid>
            )}
        </>
    )
}

const LoadingSkeleton = () => {
    return (
        <div style={{ height: 41, borderBottom: '1px #ccc solid', width: '100%' }}>
            <div style={{ height: 38, width: '100%' }}>
                <Skeleton variant="rounded" width={'100%'} height={'100%'} />
            </div>
        </div>
    )
}

export default UsersList
